<template>
  <PCSettingsBlock
    v-if="sideNavbarItems.length > 0"
    :title="t('userConfiguration.sideNavbar.title')"
  >
    <PCSettingsBlockItem :title="t('userConfiguration.sideNavbar.show.title')">
      <template #right>
        <f7-toggle
          :checked="showSideNavbar"
          @toggle:change="showSideNavbar = $event"
        />
      </template>
    </PCSettingsBlockItem>

    <PCSettingsBlock
      v-show="showSideNavbar"
      is-sortable
      :title="t('userConfiguration.sideNavbar.order.title')"
      class="padding-left"
      @sortable:sort="sortNavigationElements"
    >
      <PCSettingsBlockItem
        v-for="(item, index) in sideNavbarItems"
        :key="index"
        :title="t(`${item.title}`)"
        is-sortable
      >
        <template #right>
          <f7-toggle
            :checked="isSideNavbarSettingChecked(item)"
            @toggle:change="updateSideNavbarSettings(item.key, $event)"
          />
        </template>
      </PCSettingsBlockItem>
    </PCSettingsBlock>
  </PCSettingsBlock>
</template>

<script setup>
// @ts-check
import { onMounted, ref } from 'vue';
import { useSideNavbar } from '@/composables/useSideNavbar';
import { useSideNavbarAccess } from '@/composables/useSideNavbarAccess';
import { useI18n } from 'vue-i18n';
import { useSettingRef } from '@erpagroapp/settings';

import PCSettingsBlock from './PCSettingsBlock.vue';
import PCSettingsBlockItem from './PCSettingsBlockItem.vue';

/** @import { Ref } from 'vue' */
/** @import {
 * SideNavbarItem,
 * SideNavbarItemSettingKey,
 * SideNavbarItemSettingKeyConfigurable,
 * } from '@/composables/useSideNavbar' */

/** @type {Ref<SideNavbarItem[]>} */
const sideNavbarItems = ref([]);

const { configurableItems, sideNavbarRefs } = useSideNavbar();
const sideNavbarAccess = useSideNavbarAccess();

const { t } = useI18n();

const showSideNavbar = useSettingRef('BarraNavegacionLateral.MostrarBarra');
const sideNavbarOrder = useSettingRef('BarraNavegacionLateral.OrdenElementos');

/**
 * Updates the navigation bar settings.
 *
 * @param {SideNavbarItemSettingKey} key - The name of the setting to update.
 * @param {boolean} value - The new value.
 * @returns {void}
 */
function updateSideNavbarSettings(key, value) {
  const typedKey = /** @type {SideNavbarItemSettingKeyConfigurable} */ (key);

  if (Object.hasOwn(sideNavbarRefs, typedKey)) {
    sideNavbarRefs[typedKey].value = value;
  }
}

/**
 * Checks if a side navbar setting is checked.
 * @param {SideNavbarItem} item
 * @returns {boolean}
 */
function isSideNavbarSettingChecked(item) {
  const typedKey = /** @type {SideNavbarItemSettingKeyConfigurable} */ (item.key);
  return !!item.settingName && !!(sideNavbarRefs[typedKey]?.value);
}

/**
 * Sorts the navigation bar elements.
 *
 * @param {Record<string, any>} detail - The sortable sort event detail.
 * @returns {void}
 */
function sortNavigationElements(detail) {
  if ('from' in detail && 'to' in detail) {
    const { from, to } = detail;

    if (typeof from === 'number' && typeof to === 'number') {
      const [removedElement] = sideNavbarOrder.value.splice(from, 1);

      if (removedElement) {
        sideNavbarOrder.value.splice(to, 0, removedElement);
      }
    }
  }
}

function handleSideNavbarSettings() {
  sideNavbarItems.value = configurableItems.value
    .filter((item) => sideNavbarAccess.isItemAccessible(item))
    .map(
      (item) => ({
        ...item,
        title: item.title,
        key: item.key,
        settingName: item.settingName,
      }),
    );

  const sideNavbarOrderTemp = sideNavbarOrder.value || [];
  if (sideNavbarOrderTemp) {
    sideNavbarItems.value = sideNavbarItems.value.filter((item) => sideNavbarOrderTemp.includes(item.key));
  }
}

onMounted(() => {
  handleSideNavbarSettings();
});
</script>
